import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Banner, Button, Dialog, TextField } from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import {
  connectionTitle,
  isIcloudCalendar,
  useReconnectCaldavConnection,
  type ConnectionItem,
} from './connections';
import styles from './CaldavReconnectDialog.module.css';

const reconnectSchema = z.object({ password: z.string().min(1, 'Pflichtangabe.') });

type ReconnectValues = z.infer<typeof reconnectSchema>;

export interface CaldavReconnectDialogProps {
  /** The CalDAV connection to re-authenticate; null keeps the dialog closed. */
  item: ConnectionItem | null;
  onClose: () => void;
  onDone: (item: ConnectionItem) => void;
}

/**
 * E04 / Beratung 08 „Neu anmelden" of a CalDAV connection (D-56): asks for the new (app-specific) password
 * and verifies it through `POST /connections/:id/caldav/reconnect`; a 400 puts the field text under the
 * field and the message into the box, a 409 only the message.
 */
export function CaldavReconnectDialog({ item, onClose, onDone }: CaldavReconnectDialogProps) {
  const reconnect = useReconnectCaldavConnection();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState,
  } = useForm<ReconnectValues>({
    resolver: zodResolver(reconnectSchema),
    defaultValues: { password: '' },
  });
  const open = item !== null;

  useEffect(() => {
    if (open) {
      reset({ password: '' });
      setError(null);
      reconnect.reset();
    }
    // Reset once per opening.
  }, [open]);

  const submit = handleSubmit(async (values) => {
    if (!item) return;
    setError(null);
    try {
      const updated = await reconnect.mutateAsync({ id: item.id, password: values.password });
      onDone(updated);
    } catch (failure) {
      if (failure instanceof ApiError) {
        const message = failure.fields['password'];
        if (message) setFieldError('password', { message });
        setError(failure.message);
      } else {
        setError(errorMessage(failure));
      }
    }
  });

  const formId = 'caldav-reconnect-form';
  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={item ? connectionTitle(item) : undefined}
      title="Neu anmelden"
      subtitle={
        item && isIcloudCalendar(item)
          ? 'Geben Sie das neue app-spezifische Passwort ein.'
          : 'Geben Sie das neue Passwort ein.'
      }
      width={480}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={reconnect.isPending}>
            Neu anmelden
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <TextField
          label="Passwort"
          type="password"
          autoComplete="current-password"
          error={formState.errors.password?.message}
          {...register('password')}
        />
      </form>
    </Dialog>
  );
}
